import { makeAutoObservable, runInAction } from 'mobx';

import { isLocalEnv, isLocalOrTestEnv } from '@utils/featureFlags';

export class RequestQueue {
  private pendingRequests: Record<string, Promise<unknown>> = {};
  private cache: Record<string, unknown> = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Check if there are pending requests */
  get isFetching() {
    return Object.keys(this.pendingRequests).length > 0;
  }

  /** Check if the given request is still pending */
  isFetchingId(requestId: string) {
    return !!this.pendingRequests[requestId];
  }

  private getExistingRequest(requestId: string) {
    return this.cache[requestId] ?? this.pendingRequests[requestId];
  }

  addPendingRequest<T>(requestId: string, promise: Promise<T>) {
    this.pendingRequests[requestId] = promise;

    if (isLocalEnv) {
      console.info('[RequestQueue] added request', requestId, 'to queue', {
        ...this.pendingRequests,
      });
    }
  }

  removePendingRequest(requestId: string) {
    delete this.pendingRequests[requestId];

    if (isLocalEnv) {
      console.info('[RequestQueue] request finished', requestId);
    }
  }

  clear() {
    this.pendingRequests = {};
    this.cache = {};
  }

  async fetch<ReturnT, ErrorT = ReturnT>(
    requestId: string,
    fetchFn: () => Promise<ReturnT>,
    options: Partial<{
      ignoreCache: boolean;
      onError: (error: unknown) => ErrorT;
    }> = {}
  ): Promise<ReturnT | ErrorT> {
    // return existing request if possible
    const existingRequest = this.getExistingRequest(requestId);

    if (existingRequest && !options.ignoreCache) {
      if (isLocalEnv) {
        console.info('[RequestQueue] returning existing data for', requestId);
      }

      return existingRequest as Promise<ReturnT | ErrorT>;
    }

    const requestPromise = fetchFn()
      .then((data) => {
        // add successful request to cache
        runInAction(() => (this.cache[requestId] = data));

        return data;
      })
      .catch((err) => {
        if (isLocalOrTestEnv) {
          console.error('[RequestQueue] fetch error', requestId, err);
        }

        if (options.onError) return options.onError(err);

        throw err;
      })
      .finally(() => {
        this.removePendingRequest(requestId);
      });

    this.addPendingRequest(requestId, requestPromise);

    return requestPromise;
  }
}
